import { ChecksumAddress } from "@silo/types";
import { either } from "fp-ts";
import { constNull, identity, pipe } from "fp-ts/lib/function";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";
import { atom, useRecoilState } from "recoil";

type ImpersonationState =
  // waiting to identify if impersonation has been configured
  | { _tag: "notImpersonating" }
  // impersonation is active
  | { _tag: "impersonating"; account: ChecksumAddress }
  // impersonation has been disconnected - this state cannot be reverted in the app session
  | { _tag: "disconnected" };

type UseImpersonatedAccount = (params: {
  disconnectAccount: () => void;
  account: ChecksumAddress | undefined;
  isActive: boolean;
}) => {
  impersonatedAccount: ChecksumAddress | null;
  disconnectImpersonatedAccount: () => void;
};

// using an atom to ensure changes are global across the app session
const impersonationStateAtom = atom<ImpersonationState>({
  key: "impersonationState",
  default: { _tag: "notImpersonating" },
});

/**
 * Ideally, this logic would exist in the user state machine. The wallet/user
 * state machine could use an overhaul for v2.
 */
export const useImpersonatedAccount: UseImpersonatedAccount = ({
  isActive,
  account,
  disconnectAccount,
}) => {
  const [impersonationState, setImpersonationState] = useRecoilState(
    impersonationStateAtom
  );
  const { query } = useRouter();

  const queryParam = useMemo(
    () =>
      pipe(
        query.impersonateUser,
        ChecksumAddress.decode,
        either.fold(constNull, identity)
      ),
    [query.impersonateUser]
  );

  useEffect(() => {
    if (!queryParam) return;

    if (impersonationState._tag !== "notImpersonating") return;

    if (!isActive) return;

    if (account) {
      disconnectAccount();
    } else {
      /**
       * Only turn on impersonation if the following conditions are met:
       * - FF is enabled
       * - query param exists
       * - impersonation state is 'notImpersonating'
       * - web3 is active (i.e. already connected)
       * - there is not an active web3 wallet
       */
      setImpersonationState({ _tag: "impersonating", account: queryParam });
    }
  }, [
    queryParam,
    impersonationState,
    isActive,
    account,
    disconnectAccount,
    setImpersonationState,
  ]);

  const disconnectImpersonatedAccount = () => {
    setImpersonationState({ _tag: "disconnected" });
  };

  return {
    impersonatedAccount:
      impersonationState._tag === "impersonating"
        ? impersonationState.account
        : null,
    disconnectImpersonatedAccount,
  };
};
